import { HttpClient, provideHttpClient } from '@angular/common/http';
import {
  ApplicationConfig,
  ErrorHandler,
  Injectable,
  LOCALE_ID,
  inject,
  isDevMode,
  provideBrowserGlobalErrorListeners,
  provideAppInitializer,
  provideZoneChangeDetection,
} from '@angular/core';
import {
  provideTransloco,
  Translation,
  TranslocoLoader,
  TranslocoService,
} from '@jsverse/transloco';

import { getApp, initializeApp, provideFirebaseApp } from '@angular/fire/app';
import {
  browserLocalPersistence,
  initializeAuth,
  provideAuth,
} from '@angular/fire/auth';
import { initializeFirestore, provideFirestore } from '@angular/fire/firestore';
import {
  connectFunctionsEmulator,
  getFunctions,
  provideFunctions,
} from '@angular/fire/functions';
import { getStorage, provideStorage } from '@angular/fire/storage';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import {
  Router,
  provideRouter,
  withComponentInputBinding,
  withNavigationErrorHandler,
} from '@angular/router';
import { ENVIRONMENT, TenantConfig } from '@models';
import Aura from '@primeng/themes/aura';
import * as Sentry from '@sentry/angular';
import {
  AuthService,
  HostnameTenantService,
  SEASON_PERSISTENCE_ENABLED,
  TenantContextService,
  TenantFeaturesService,
} from '@services';
import {
  provideStaleChunkReloadReset,
  reloadOnStaleChunkError,
} from '@utils/lib/stale-chunk-reload';
import { providePrimeNG } from 'primeng/config';
import { prime } from './primeng.i18n';
import { filter, first, firstValueFrom, of, switchMap, tap } from 'rxjs';
import { environment } from '../environments/environment';
import { appRoutes } from './app.routes';

@Injectable({ providedIn: 'root' })
export class HttpLoader implements TranslocoLoader {
  private http = inject(HttpClient);
  getTranslation(lang: string) {
    return this.http.get<Translation>(`/assets/i18n/${lang}.json`);
  }
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideZoneChangeDetection({ eventCoalescing: true }),
    // After a deploy, a tab with the old build asks for chunk-*.js files that no
    // longer exist: reload once to pick up the new index.html.
    provideRouter(
      appRoutes,
      withComponentInputBinding(),
      withNavigationErrorHandler(reloadOnStaleChunkError),
    ),
    provideStaleChunkReloadReset(),
    provideAnimationsAsync(),
    provideHttpClient(),
    provideFirebaseApp(() => initializeApp(environment.firebase)),
    provideAuth(() =>
      initializeAuth(getApp(), { persistence: browserLocalPersistence }),
    ),
    provideFirestore(() =>
      initializeFirestore(getApp(), { ignoreUndefinedProperties: true }),
    ),
    provideFunctions(() => {
      const functions = getFunctions(getApp(), 'europe-west1');
      if ((environment as any).emulator) {
        connectFunctionsEmulator(functions, 'localhost', 5001);
      }
      return functions;
    }),
    provideStorage(() => getStorage()),
    providePrimeNG({
      theme: { preset: Aura, options: { darkModeSelector: false } },
      translation: prime,
    }),
    { provide: LOCALE_ID, useValue: 'es-ES' },
    { provide: ENVIRONMENT, useValue: environment },
    // El año de temporada de la topbar es un filtro explícito y siempre visible:
    // perderlo en cada F5 es un bug. Solo el admin lo persiste (por tenant);
    // el resto de apps mantienen la selección en memoria (default del token).
    { provide: SEASON_PERSISTENCE_ENABLED, useValue: true },
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({ showDialog: false }),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    provideAppInitializer(() => {
      inject(Sentry.TraceService);
    }),
    // Resolve current hostname to a tenant (pre-auth) so the login page
    // can display tenant branding before the user authenticates. On custom
    // domains this also swaps the browser tab title/favicon from the generic
    // "DMS Manager" to the tenant's own name and logo.
    provideAppInitializer(() => {
      const hostnameTenant = inject(HostnameTenantService);
      hostnameTenant.applyBrowserTabBranding();
      return hostnameTenant.resolve();
    }),
    // Wait for Firebase Auth to resolve before any route or guard runs.
    // This prevents "tenantId not yet resolved" errors on hard refresh.
    provideAppInitializer(() => {
      const auth = inject(AuthService);
      return firstValueFrom(auth.isLoggedIn$);
    }),
    // For authenticated users: wait for TenantConfig to load (and enterprise Firebase
    // project switch to happen) before routing. Prevents data reads against the wrong
    // Firestore project on first load.
    // Also sets the Sentry tenant tag so all errors are filterable by tenantId.
    provideAppInitializer(() => {
      const tenantCtx = inject(TenantContextService);
      const features = inject(TenantFeaturesService);
      return firstValueFrom(
        tenantCtx.claimsOrNull$.pipe(
          first(),
          switchMap((claims) => {
            if (!claims) return of(null);
            // Super admins have no tenantId in their claims — tenantConfig$ would
            // never emit, blocking the initializer and causing a blank screen on refresh.
            if (claims.superAdmin === true) return of(null);
            return features.tenantConfig$.pipe(
              filter((c): c is TenantConfig => c !== null),
              first(),
              tap((config) => {
                Sentry.setTag('tenantId', config.id);
              }),
            );
          }),
        ),
      );
    }),
    provideTransloco({
      config: {
        availableLangs: ['es'],
        defaultLang: 'es',
        reRenderOnLangChange: true,
        prodMode: !isDevMode(),
      },
      loader: HttpLoader,
    }),
    // Preload default language before the app renders
    provideAppInitializer(() => {
      const transloco = inject(TranslocoService);
      return firstValueFrom(transloco.load('es'));
    }),
  ],
};
